import { useState } from "react";
import { Reveal } from "@/components/Reveal";
import { Check, Sparkle } from "@phosphor-icons/react";
import { paymentsApi } from "@/lib/apiClient";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

const PLANS_CONFIG = [
    { key: null, nameKey: "pricing.plans.free.name", defaultName: "Free", price: "0", periodKey: "pricing.plans.free.period", pitchKey: "pricing.plans.free.pitch", featuresKey: "pricing.plans.free.features" },
    { key: "passport_plus_monthly", nameKey: "pricing.plans.plus.name", defaultName: "Passport Plus", price: "5.99", periodKey: "pricing.plans.plus.period", pitchKey: "pricing.plans.plus.pitch", featuresKey: "pricing.plans.plus.features" },
    { key: "passport_pro_monthly", nameKey: "pricing.plans.pro.name", defaultName: "Passport Pro", price: "12.99", periodKey: "pricing.plans.pro.period", pitchKey: "pricing.plans.pro.pitch", featuresKey: "pricing.plans.pro.features", featured: true },
    { key: "passport_elite_monthly", nameKey: "pricing.plans.elite.name", defaultName: "Passport Elite", price: "24.99", periodKey: "pricing.plans.elite.period", pitchKey: "pricing.plans.elite.pitch", featuresKey: "pricing.plans.elite.features" },
];

export default function Pricing() {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(null);

    const checkout = async (lookup_key) => {
        if (!lookup_key) return;
        setLoading(lookup_key);
        try {
            const { checkout_url } = await paymentsApi.checkout(lookup_key);
            window.location.href = checkout_url;
        } catch (e) {
            toast.error(t("pricing.toasts.checkoutFailed"));
            setLoading(null);
        }
    };

    return (
        <section id="pricing" className="relative py-32 md:py-40" data-testid="pricing">
            <div className="max-w-[1400px] mx-auto px-6 md:px-10">
                <Reveal>
                    <div className="text-center mb-20 max-w-3xl mx-auto">
                        <div className="overline mb-4">{t("pricing.overline")}</div>
                        <h2 className="font-serif text-5xl md:text-7xl text-white leading-[0.95] tracking-tighter">
                            {t("pricing.titleLine1")} <span style={{ fontStyle: "italic", color: "var(--gold-pale)" }}>{t("pricing.titleLine2")}</span>.
                        </h2>
                        <p className="text-white/60 text-lg leading-relaxed mt-6">
                            {t("pricing.subtitle")}
                        </p>
                    </div>
                </Reveal>

                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {PLANS_CONFIG.map((p, i) => {
                        const name = t(p.nameKey);
                        const pitch = t(p.pitchKey);
                        const period = t(p.periodKey);
                        const features = t(p.featuresKey, { returnObjects: true }) || [];
                        return (
                            <Reveal key={p.defaultName} delay={0.05 * i}>
                                <div
                                    className="relative rounded-3xl p-8 h-full flex flex-col card-hover"
                                    style={{
                                        background: p.featured
                                            ? "linear-gradient(180deg, rgba(212,175,55,0.10), rgba(212,175,55,0.02))"
                                            : "var(--navy-deep)",
                                        border: p.featured
                                            ? "1px solid rgba(212,175,55,0.4)"
                                            : "1px solid rgba(255,255,255,0.06)",
                                    }}
                                    data-testid={`plan-${p.defaultName.toLowerCase().replace(/\s/g, '-')}`}
                                >
                                    {p.featured && (
                                        <div className="absolute -top-3 left-8 px-3 py-1 rounded-full text-[10px] tracking-[0.2em] uppercase" style={{ background: "var(--gold)", color: "var(--navy)" }}>
                                            <span className="inline-flex items-center gap-1"><Sparkle size={10} weight="fill" /> {t("pricing.mostLoved")}</span>
                                        </div>
                                    )}
                                    <div className="font-serif text-3xl text-white leading-none mb-2">{name}</div>
                                    <div className="text-white/50 text-sm mb-8">{pitch}</div>

                                    <div className="flex items-baseline gap-2 mb-1">
                                        <span className="text-2xl text-white/60">$</span>
                                        <span className="font-serif text-6xl text-white leading-none">{p.price}</span>
                                    </div>
                                    <div className="text-xs tracking-[0.2em] uppercase text-white/50 mb-8">{period}</div>

                                    <ul className="space-y-3 mb-10 flex-1">
                                        {Array.isArray(features) && features.map((f) => (
                                            <li key={f} className="flex items-start gap-3 text-sm text-white/80">
                                                <span className="mt-0.5 w-4 h-4 rounded-full grid place-items-center shrink-0" style={{ background: "rgba(212,175,55,0.12)" }}>
                                                    <Check size={10} weight="bold" color="#D4AF37" />
                                                </span>
                                                {f}
                                            </li>
                                        ))}
                                    </ul>

                                    {p.key ? (
                                        <button
                                            onClick={() => checkout(p.key)}
                                            disabled={loading === p.key}
                                            className={p.featured ? "btn-gold w-full justify-center" : "btn-ghost w-full justify-center"}
                                            data-testid={`plan-cta-${p.defaultName.toLowerCase().replace(/\s/g, '-')}`}
                                        >
                                            {loading === p.key ? t("pricing.redirecting") : t("pricing.choosePlan")}
                                        </button>
                                    ) : (
                                        <a href="/auth?mode=signup" className="btn-ghost w-full justify-center" data-testid={`plan-cta-free`}>
                                            {t("pricing.startFree")}
                                        </a>
                                    )}
                                </div>
                            </Reveal>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
